import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import $ from 'jquery';
import Swal from 'sweetalert2';
import { useParams, Link, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'datatables.net';
import 'datatables.net-buttons';
import 'datatables.net-buttons-dt/css/buttons.dataTables.min.css';
import 'datatables.net-buttons/js/buttons.colVis';
import 'datatables.net-buttons/js/buttons.html5';
import 'datatables.net-buttons/js/buttons.print';
import { baseurl } from '../utility/helper';

const VALID_SORT_COLUMNS = [
  'membership_no', 'first_name', 'gender', 'mobile_no',
  'email', 'date_joining', 'membership_exp_date', 'status_name', 'member_id'
];

const MemberList = () => {
  const { chapterId } = useParams();
  const navigate      = useNavigate();
  const roleid        = parseInt(sessionStorage.getItem('userroleId'));
  const userChapter   = sessionStorage.getItem('userchapterId') || '';
  const token         = sessionStorage.getItem('token');
  const chaptername   = sessionStorage.getItem('userchaptername');
  const username      = sessionStorage.getItem('username');
  const mobileno      = sessionStorage.getItem('mobileno');

  const [totalMembers,   setTotal]      = useState(null);
  const [activeMembers,  setActive]     = useState(null);
  const [expiredMembers, setExpire]     = useState(null);
  const [MigDes,         setMigDes]     = useState(null);
  const [Doesnotrenew,   setDoesnotrenew]     = useState(null);
  const [DoesnotrenewPercent,         setDoesnotrenewPercent]     = useState(0);
  const [activePercent,  setPercent]    = useState(0);
  const [expPercent,     setexpPercent] = useState(0);
  const [migDesPercent, setMigDesPercent] = useState(0);
  const [memberFilter,   setMemberFilter] = useState('');
  const [chapterAdmin,   setChapterAdmin] = useState('');
  const [tableLoading,   setTableLoading] = useState(true);

  // Ref so the DataTable ajax closure always reads the latest filter value
  const memberFilterRef = useRef(memberFilter);
  useEffect(() => { memberFilterRef.current = memberFilter; }, [memberFilter]);

  // Skip the filter-reload effect on the very first render — the main
  // init effect already fetches the initial data for the default filter.
  const isFirstFilterRun = useRef(true);

  // ── Derived base URL (stable reference for DataTable closure) ───────────────
  const effectiveChapterId = chapterId
    ? chapterId
    : (roleid === 2 && userChapter ? userChapter : null);

  const membersBaseUrl = effectiveChapterId
    ? `${baseurl}/admin/members?chapterId=${effectiveChapterId}`
    : `${baseurl}/admin/members`;

  // ── Fetch summary counts ────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      Swal.fire({ icon: 'error', title: 'Token Missing', text: 'Please login again.' });
      return;
    }
    fetchMemberCount();
    fetchChapterAdmin();
  }, [chapterId, token]);

  const fetchMemberCount = async () => {
    try {
      let url = `${baseurl}/admin/memberscount`;
      if (chapterId) {
        url += `?chapterId=${chapterId}`;
      } else if (userChapter && roleid === 2) {
        url += `?chapterId=${userChapter}`;
      }
      const response = await fetch(url, {
        method: 'GET',
        headers: { Authorization: token, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      setTotal(result.totalMembers   || 0);
      setActive(result.activeMembers || 0);
      setExpire(result.expiredMembers || 0);
      setMigDes(result.migratedDeceased || 0);
      setDoesnotrenew(result.notRenewedMembers || 0);
      setPercent(result.totalMembers > 0 ? (result.activeMembers  / result.totalMembers) * 100 : 0);
      setexpPercent(result.totalMembers > 0 ? (result.expiredMembers / result.totalMembers) * 100 : 0);
      setMigDesPercent(result.totalMembers > 0 ? (result.migratedDeceased / result.totalMembers) * 100 : 0);
      setDoesnotrenewPercent(result.totalMembers > 0 ? (result.notRenewedMembers / result.totalMembers) * 100 : 0);
    } catch (err) {
      console.error('Error fetching member count:', err);
      Swal.fire({ icon: 'error', title: 'Fetch Failed', text: 'Unable to fetch member count.' });
    }
  };

  const fetchChapterAdmin = async () => {
    try {
      const response = await fetch(
        `${baseurl}/admin/chapter-admin?chapterId=${chapterId}`,
        { method: 'GET', headers: { Authorization: token, 'Content-Type': 'application/json' } }
      );
      const data = await response.json();
      setChapterAdmin(data);
    } catch (err) {
      console.error(err);
    }
  };

  // ── DataTable init ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      Swal.fire({ icon: 'error', title: 'Token Missing', text: 'Please login again.' });
      return;
    }

    setTableLoading(true);

    if ($.fn.dataTable.isDataTable('#memberlist')) {
      $('#memberlist').DataTable().destroy();
    }

    // If the effect's cleanup fires while the pre-fetch is still in-flight
    // (dependency change, hot-reload, StrictMode double-invoke), this flag
    // tells the async function to bail out instead of calling .DataTable()
    // on a table that was already destroyed or re-initialised.
    let cancelled = false;

    const initDataTable = async () => {
      // ── Step 1: Pre-fetch first 25 rows for an instant first render ──────
      let initialData      = [];
      let totalRecords     = 0;
      let filteredRecords  = 0;

      try {
        const preload = await axios.get(membersBaseUrl, {
          headers: { Authorization: token },
          params: {
            draw:        1,
            start:       0,
            length:      25,
            search:      '',
            orderColumn: 'member_id',
            orderDir:    'desc',
            status:      memberFilterRef.current,
            memberType:  'renewed'
          }
        });
        initialData     = preload.data.data            || [];
        totalRecords    = preload.data.recordsTotal    || 0;
        filteredRecords = preload.data.recordsFiltered || totalRecords;
      } catch (err) {
        console.error('Pre-fetch failed:', err);
        // Table still initialises below — just with an empty first render
      }

      // Cleanup ran while we were awaiting — do not touch the DOM.
      if (cancelled) return;

      // Guard: a fast re-render could have snuck in another init during the
      // await. Destroy it before we proceed so we never double-initialise.
      if ($.fn.dataTable.isDataTable('#memberlist')) {
        $('#memberlist').DataTable().destroy();
      }

      // ── Step 2: Init DataTable with pre-fetched rows ──────────────────────
      // `data`         → seeds the table immediately (no spinner wait)
      // `deferLoading` → tells DataTables the true total so it shows the
      //                  correct "Showing 1–25 of N entries" footer, and
      //                  suppresses the automatic first AJAX call.
      //                  AJAX only fires when the user sorts / searches /
      //                  changes the page length or navigates pages.
      $('#memberlist').DataTable({
        autoWidth:  false,
        processing: true,
        responsive: false,
        serverSide: true,

        data:        initialData,
        deferLoading: [filteredRecords, totalRecords],

        ajax: (dtData, callback) => {
          const orderInfo = dtData.order?.[0] ?? null;

          const rawOrderColumn = orderInfo
            ? dtData.columns[orderInfo.column].name
            : 'member_id';
          const safeOrderColumn = VALID_SORT_COLUMNS.includes(rawOrderColumn)
            ? rawOrderColumn
            : 'member_id';

          axios.get(membersBaseUrl, {
            headers: { Authorization: token },
            params: {
              draw:        dtData.draw,
              start:       dtData.start,
              length:      dtData.length,
              search:      dtData.search.value,
              orderColumn: safeOrderColumn,
              orderDir:    orderInfo ? orderInfo.dir : 'desc',
              status:      memberFilterRef.current,
              memberType:  'renewed'
            }
          })
            .then(res => callback({
              draw:            dtData.draw,
              recordsTotal:    res.data.recordsTotal,
              recordsFiltered: res.data.recordsFiltered,
              data:            res.data.data
            }))
            .catch(() =>
              Swal.fire({ icon: 'error', title: 'Fetch Failed', text: 'Unable to load members.' })
            );
        },

        columns: [
          {
            data:   null,
            name:   'Sr.No',
            render: (data, type, row, meta) => meta.row + meta.settings._iDisplayStart + 1
          },
          {
            data: 'membership_no',
            name: 'membership_no',
            render: (data, type, row) => `
              <a
                href="/admin/EditMember?memberId=${row.member_id}&edit=false"
                class="preview-member"
                onclick="event.preventDefault(); window.open(this.href, '_blank');"
              >
                ${data}
              </a>
            `
          },
          { data: 'chapter_id', name: 'Chapter ID' },
          {
            data:   null,
            name:   'first_name',
            render: row => `${row.first_name || ''} ${row.last_name || ''}`
          },
          {
            data:   'profile_pic',
            name:   'profile_pic',
            render: data =>
              data
                ? `<img src="${data}" style="width:50px;height:50px;object-fit:cover;border-radius:4px;" />`
                : `<span>No Image</span>`
          },
          {
            data:   'mobile_no',
            name:   'mobile_no',
            render: data => data ? data.split(',').map(n => n.trim()).join('<br>') : ''
          },
          { data: 'email', name: 'email' },
          {
            data:   null,
            name:   'address',
            render: (data, type, row) => {
              const address = [
                row.present_address, row.present_city,
                row.present_state,   row.present_pincode
              ]
                .filter(val => val && val !== '')
                .join(', ');
              return address || '-';
            }
          },
          { data: 'dob', name: 'DOB', render: data => formatDateWithSuffix(data)},
          { data: 'gender', name: 'gender' },
          {
            data:   'date_joining',
            name:   'date_joining',
            render: data => formatDateWithSuffix(data)
          },
          {
            data:   'membership_exp_date',
            name:   'membership_exp_date',
            render: data => formatDateWithSuffix(data)
          },
          {
            data:   'last_renewal_date',
            name:   'last_renewal_date',
            render: data => formatDateWithSuffix(data)
          },
          {
            data:   'status_name',
            name:   'status_name',
            render: data => `<center>${data || ''}</center>`
          }
        ],

        dom:        'Blfrtip',
        pageLength: 25,
        lengthMenu: [
          [10, 25, 50, 100, 250, 500, -1],
          [10, 25, 50, 100, 250, 500, 'All']
        ],
        buttons: [
          { extend: 'copyHtml5',  className: 'btn btn-primary dttable', exportOptions: { columns: ':visible' } },
          { extend: 'excelHtml5', className: 'btn btn-primary dttable', exportOptions: { columns: ':visible' } },
          { extend: 'csvHtml5',   className: 'btn btn-primary dttable', exportOptions: { columns: ':visible' } },
          { extend: 'pdfHtml5',   className: 'btn btn-primary dttable', exportOptions: { columns: ':visible' } },
          { extend: 'print',      className: 'btn btn-primary dttable', exportOptions: { columns: ':visible' } }
        ],
        order: [[0, 'desc']],
        initComplete: function () {
          $('.dt-length select').addClass('form-select form-select-sm');
        }
      });

      // ── Event delegation ────────────────────────────────────────────────
      $('#memberlist')
        .off('click', '.delete-member')
        .on('click', '.delete-member', function () {
          handleDelete($(this).data('id'));
        });


      $(document)
        .off('click', '.edit-member')
        .on('click', '.edit-member', function (e) {
          e.preventDefault();

          const memberId = $(this).data('id');

          navigate(`/admin/EditMember?memberId=${memberId}&edit=true`);
        });

      // Pre-fetch done, DataTable painted — hide the loader
      if (!cancelled) setTableLoading(false);
    };

    initDataTable();

    return () => {
      cancelled = true; // stop the async init from running after this point
      if ($.fn.dataTable.isDataTable('#memberlist')) {
        $('#memberlist').DataTable().destroy();
      }
      $(document).off('click', '.preview-member');
      $(document).off('click', '.edit-member');
    };
  }, [chapterId, roleid, userChapter, token]);

  // ── Reload on filter card click ─────────────────────────────────────────────
  // When the user clicks a summary card the filter state updates. We show a
  // "please wait" modal for the duration of the AJAX reload and close it in
  // the callback DataTables fires once the new rows have rendered.
  useEffect(() => {
    // The main init effect already covers the first render for the default
    // filter — don't pop the modal for that pass.
    if (isFirstFilterRun.current) {
      isFirstFilterRun.current = false;
      return;
    }

    if ($.fn.dataTable.isDataTable('#memberlist')) {
      Swal.fire({
        title: 'Please wait...',
        text: 'Filtering members',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading()
      });

      // 2nd arg `false` keeps the current page instead of resetting to page 1.
      // Change to `true` if you'd rather always land back on page 1 after a filter change.
      $('#memberlist').DataTable().ajax.reload(() => {
        Swal.close();
      }, false);
    }
  }, [memberFilter]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  function formatDateWithSuffix(dateStr) {
    if (!dateStr) return '';
    const date  = new Date(dateStr);
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(date);
    const year  = parts.find(p => p.type === 'year').value;
    const month = parts.find(p => p.type === 'month').value;
    const day   = parts.find(p => p.type === 'day').value;
    return `${year}-${month}-${day}`;
  }

  const handleDelete = memberId => {
    Swal.fire({
      title: 'Are you sure?', text: 'This action cannot be undone!',
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Yes, delete it!', cancelButtonText: 'Cancel'
    }).then(result => {
      if (!result.isConfirmed) return;
      axios
        .delete(`${baseurl}/admin/member/${memberId}`, { headers: { Authorization: token } })
        .then(res => {
          Swal.fire('Deleted!', res.data.message || 'Member deleted.', 'success');
          $('#memberlist').DataTable().ajax.reload();
        })
        .catch(err =>
          Swal.fire('Error!', err.response?.data?.message || 'Delete failed.', 'error')
        );
    });
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="content container-fluid">

      {/* Header bar */}
      <div className="row bg-white p-3 rounded shadow-sm align-items-center mb-3">
        <div className="col-12 col-md-4 mb-2 mb-md-0">
          <p className="mb-0">
            <span className="fw-semibold text-secondary">Chapter</span>{' '}
            {chapterId && chapterAdmin ? (
              <>{chapterAdmin.chapter_id}: <b>{chapterAdmin.chapter_name}</b></>
            ) : (
              <>{userChapter}: <b>{chaptername}</b></>
            )}
          </p>
        </div>

        <div className="col-12 col-md-4 text-center mb-2 mb-md-0">
          {chapterId && chapterAdmin ? (
            <div>
              <span className="user-details">Chapter Admin: </span>
              <b>{chapterAdmin.name}</b>
            </div>
          ) : (
            <>
              {roleid === 1 && (
                <div><span className="user-details">Chapter SuperAdmin: </span><b>{username}</b></div>
              )}
              {roleid === 2 && (
                <div><span className="user-details">Chapter Admin: </span><b>{username}</b></div>
              )}
            </>
          )}
        </div>

        <div className="col-12 col-md-4 text-md-end">
          <p className="mb-0">
            <span className="fw-semibold text-secondary">Mobile No :</span>{' '}
            <b>{chapterId && chapterAdmin ? chapterAdmin.mobileno : mobileno}</b>
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="row" style={{ display: 'flex', flexWrap: 'wrap', margin: 0 }}>
        {[
          { key: '',                  label: 'Total Members',         count: totalMembers,      percent: 100 },
          { key: 'active',            label: 'Active Members',        count: activeMembers,     percent: activePercent },
          { key: 'expired',           label: 'Lapsed Membership',     count: expiredMembers,    percent: expPercent },
          { key: 'migrated_deceased', label: 'Migrated & Deceased',   count: MigDes,            percent: migDesPercent },
          { key: 'not_renewed',       label: "Doesn't want to renew", count: Doesnotrenew,      percent: DoesnotrenewPercent },
        ].map((card) => (
          <div
            key={card.key || 'total'}
            style={{ flex: '1 1 0', minWidth: '180px', padding: '0 8px' }}
          >
            <div
              className="card mb-2"
              style={{
                cursor: 'pointer',
                border: memberFilter === card.key ? '2px solid #0d6efd' : '1px solid #dee2e6',
                boxShadow: memberFilter === card.key ? '0 0 0 3px rgba(13,110,253,.2)' : 'none',
                transition: 'box-shadow 0.2s, border 0.2s',
              }}
              onClick={() => setMemberFilter(card.key)}
            >
              <div className="card-body" style={{padding:'1rem'}}>
                <p>{card.label}</p>
                <div className="dash-widget-header">
                    <div className="dash-title">
                      <p><h4><b>{card.count}</b></h4></p>
                    </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="container mt-4">
        <div className="profile-section">
          <div className="d-flex justify-content-end mb-3">
            <div style={{ width: '100%', textAlign: '-webkit-right', paddingRight: '37px', fontSize: 'medium' }}>
              <button style={{ border: 'none', padding: 0, margin: 0, cursor: 'pointer' }}>
                <Link to="/admin/MemberForm" style={{ display: 'block', padding: '0.5rem 1rem', textDecoration: 'none', color: 'inherit' }}>
                  <i className="bx bxs-plus-circle mr-2"></i> Add Member
                </Link>
              </button>
            </div>
          </div>

          <br />
          <br />

          {/* Loader overlay — visible during the pre-fetch, hidden once DataTable paints */}
          {tableLoading && (
            <div style={{
              display:        'flex',
              flexDirection:  'column',
              alignItems:     'center',
              justifyContent: 'center',
              minHeight:      '260px',
              gap:            '16px',
              background:     '#fff',
              borderRadius:   '8px',
              border:         '1px solid #dee2e6',
            }}>
              {/* Spinner */}
              <div
                style={{
                  width:       '48px',
                  height:      '48px',
                  border:      '4px solid #e9ecef',
                  borderTop:   '4px solid #0d6efd',
                  borderRadius:'50%',
                  animation:   'memberlist-spin 0.8s linear infinite',
                }}
              />
              {/* Pulsing skeleton rows */}
              <div style={{ width: '100%', maxWidth: '600px', padding: '0 24px' }}>
                {[100, 85, 92, 78, 88].map((w, i) => (
                  <div
                    key={i}
                    style={{
                      height:       '14px',
                      width:        `${w}%`,
                      background:   '#e9ecef',
                      borderRadius: '4px',
                      marginBottom: '10px',
                      animation:    `memberlist-pulse 1.4s ease-in-out ${i * 0.12}s infinite`,
                    }}
                  />
                ))}
              </div>
              <p style={{ margin: 0, color: '#6c757d', fontSize: '14px' }}>
                Loading members…
              </p>
              {/* Keyframes injected once via a style tag */}
              <style>{`
                @keyframes memberlist-spin {
                  to { transform: rotate(360deg); }
                }
                @keyframes memberlist-pulse {
                  0%, 100% { opacity: 1; }
                  50%       { opacity: 0.4; }
                }
              `}</style>
            </div>
          )}

          <div className="table-responsive" style={{ display: tableLoading ? 'none' : 'block' }}>
            <table id="memberlist" className="table table-bordered table-striped" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Sr.No</th>
                  <th>Membership No</th>
                  <th>Chapter ID</th>
                  <th>Name</th>
                  <th>PI</th>
                  <th>Mobile</th>
                  <th>Email ID</th>
                  <th>Address</th>
                  <th>DOB</th>
                  <th>Gender</th>
                  <th>Joining Date</th>
                  <th>Expiry Date</th>
                  <th>Last Renewal Date</th>
                  <th>Membership Term</th>
                </tr>
              </thead>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberList;